import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Endpoints } from 'src/app/core/resources/endpoints';
import { Global } from 'src/app/core/resources/global';
import { environment } from 'src/environments/environment';
import { Utils } from 'src/app/core/resources/utils';
import { Company } from 'src/app/core/interfaces/company';
import { ModelRoleComany, RoleComany } from 'src/app/core/interfaces/roleCompany';
import { Observable } from 'rxjs';
import { CostCenter } from 'src/app/core/interfaces/costCenter';
import __costCenters from '__mock/costCenters.json';
import { TreeFolder } from 'src/app/core/interfaces/treeFolder';
import { AuthService } from 'src/app/core/services/authentication.service';
import { AdditionalCost } from 'src/app/core/interfaces/additionalCost';
import { WhoToPay } from 'src/app/core/interfaces/whoToPay';
import { PaymentType } from 'src/app/core/interfaces/paymentType';
import { CompanyResource } from 'src/app/core/interfaces/companyResource';
import { Tag } from 'src/app/core/interfaces/tag';
import { map, tap, timeout } from 'rxjs/operators';
import { ListHistoryResponse } from 'src/app/core/interfaces/list-history';
import { ValidationRule } from 'src/app/core/interfaces/validation-rule';
import { CompanyCompliment, CompanyComplimentDTO, ComplimentTitle } from 'src/app/core/interfaces/companyCompliment';
import { VehicleDocumentRequired } from 'src/app/core/interfaces/vehicleDocuments';
import { Companies } from 'src/app/core/resources/companies';
import { Fmt } from 'src/app/core/messages/fmt';
import { GuideNovelty } from 'src/app/core/interfaces/guideNovelty';
import { TypeBill } from 'src/app/core/interfaces/typeBill';
import { BillingUser } from 'src/app/core/interfaces/billingUser';
import { RndcCredentials } from 'src/app/core/interfaces/rndcCredentials';
import { EmailStructure } from 'src/app/core/interfaces/emailStructure';
import { EventEmitter } from '@angular/core';
import { FirebaseApi } from 'src/app/core/classes/firebase-api';
import { DateManager } from 'src/app/core/managers/date.manager';
import { Permission } from 'src/app/core/resources/permission';
import { SaasTransportCompany } from 'src/app/core/interfaces/saasTransportCompany';
import { InsuranceInformation } from 'src/app/core/interfaces/insuranceInformation';
import { SaasTransportCompanyService } from 'src/app/core/services/saas-transport-company.service';
import { PreBillField } from 'src/app/core/interfaces/preBillField';

/** Reclasificación en lote del perfil de riesgo: solo se envían las empresas que cambiaron de perfil. */
export interface RiskProfileBulkBody {
  highRiskCompanyIds: string[]
  mediumRiskCompanyIds: string[]
  lowRiskCompanyIds: string[]
}

@Injectable({
  providedIn: 'root'

})
export class CompaniesService {

  compnySelected: Company;
  private mockCostCenters: CostCenter[] = __costCenters;
  unsubscribeCompanyChanges: { companyId: string, id: string, unsubscribe: () => void, eventEmitter: EventEmitter<Company> }[] = [];

  constructor(
    private http: HttpClient,
    private endpointResources: Endpoints,
    private globalResources: Global,
    public utils: Utils,
    private modelRoleComany: ModelRoleComany,
    private authService: AuthService,
    private saasTransportCompanyService: SaasTransportCompanyService,
  ) {

  }

  public getListCompaniesChildren(nit: string, pageKey: number, pageSize: number) {
    return this.http.get(
      `${environment.urlServerTeclogi}${this.endpointResources.getAllCompaniesChildrens}${nit}`
    );
  }

  public getHistoryCompanies(
    companyId?: string,
    serviceType?: string[],
    startDate?: string,
    endDate?: string
  ): Observable<ListHistoryResponse> {
    let params = new HttpParams();
    const data = {
      companyId,
      serviceType,
      startDate,
      endDate
    };
    for (let [key, value] of Object.entries(data)) {
      if (value) {
        if (Array.isArray(value)) value = JSON.stringify(value);
        params = params.append(key, value);
      }
    }
    return this.http.get<ListHistoryResponse>(
      `${environment.urlServerTeclogi}${this.endpointResources.getHistoryCompanies}${params}`
    );
  }

  public getReportHistoryCompanies(
    companyId?: string,
    serviceTypes?: string[],
    startDate?: string,
    endDate?: string,
    email = false,
    excel = true
  ) {
    let params = new HttpParams();
    const data = {
      companyId,
      serviceTypes,
      startDate,
      endDate,
      email,
      excel
    };
    for (let [key, value] of Object.entries(data)) {
      if (this.utils.isDefined(value)) {
        if (Array.isArray(value)) value = JSON.stringify(value);
        params = params.append(key, value.toString());
      }
    }
    return this.http.get(
      `${environment.urlServerTeclogi}${this.endpointResources.getReportHistoryCompanies}`,
      { params, observe: 'response', responseType: 'blob' }
    ).pipe(timeout(600000));
  }

  public getAdditionalServices(transportCompanyId?: string): Observable<AdditionalCost[]> {
    return this.http.get<AdditionalCost[]>(environment.urlServerTeclogi + this.endpointResources.companyAdditionalCost,
      { params: this.transportCompanyParams(transportCompanyId) });
  }

  public getWhoToPays(): Observable<WhoToPay[]> {
    return this.http.get<WhoToPay[]>(`${environment.urlServerTeclogi}${this.endpointResources.urlWhoToPays}`)
  }

  /** Guarda las utilidades de todos los servicios adicionales en un solo PUT (arreglo de servicios). */
  public saveAdditionalCostsUtilities(body: AdditionalCost[], transportCompanyId?: string) {
    return this.http.put(environment.urlServerTeclogi + this.endpointResources.companyAdditionalCost, body,
      { params: this.transportCompanyParams(transportCompanyId) });
  }


  public createCompany(companyData: Company) {

    let url = `${environment.urlServerTeclogi}${this.endpointResources.urlCreateCompany}`;

    return this.http.post(
      url,
      companyData
    ).pipe(
      // La empresa creada puede ser una nueva transportadora, así que el listado
      // cacheado de transportadoras de la SaaS puede haber cambiado.
      tap(() => this.saasTransportCompanyService.clearCache())
    );
  }

  public updateCompany(companyData: Company) {
    return this.http.put(
      `${environment.urlServerTeclogi}${this.endpointResources.updateCompany}`,
      companyData
    ).pipe(
      // La empresa actualizada puede ser una transportadora renombrada, así que el
      // listado cacheado de transportadoras de la SaaS puede haber cambiado.
      tap(() => this.saasTransportCompanyService.clearCache())
    );
  }

  // Actualiza en lote el perfil de riesgo (riesgo bajo/medio/alto) de las empresas indicadas.
  public updateRiskProfileBulk(body: RiskProfileBulkBody) {
    return this.http.put(
      `${environment.urlServerTeclogi}${this.endpointResources.updateRiskProfileBulk}`,
      body
    );
  }

  public updateCompanyRndcCredentials(rndcCredentials: RndcCredentials) {
    let params = new HttpParams();
    params = params.append('username', rndcCredentials.userName);
    params = params.append('password', rndcCredentials.password);
    params = params.append('channel', 'rndc');
    return this.http.post(`${environment.urlServerTeclogi}${this.endpointResources.urlUpdateCompanyRndcCredentials}`, null, { params });
  }

  public deleteCompany(nit: string) {
    return this.http.post(
      environment.urlServerTeclogi + this.endpointResources.deleteCompany,
      [nit]
    );
  }

  public uploadEmailByCompany(storageName: string, fileExternsion: string, typeName: string, body: string) {
    return this.http.post(
      `${environment.urlServerTeclogi}${this.endpointResources.addEmailCompany}storageName=${storageName}&fileExternsion=${fileExternsion}&fileType=${typeName}`,
      body
    );
  }

  public uploadResourcesCompany(companyId: string, body: CompanyResource) {
    return this.http.post(
      `${environment.urlServerTeclogi}${this.endpointResources.companyFile}?companyId=${companyId}`,
      body
    );
  }

  public getResourcesCompany(companyId: string): Observable<CompanyResource[]> {
    return this.http.get<CompanyResource[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.getCompanyFile}?companyId=${companyId}`
    );
  }

  public createMultipleRole(rolesCompanies: RoleComany[]) {
    const url = `${environment.urlServerTeclogi}${this.endpointResources.createRole}`;
    return this.http.post(url, rolesCompanies);
  }

  public getModelRole(nit: string): RoleComany[] {
    let listRoles: RoleComany[] = [];

    let adminDefaultCompany = this.modelRoleComany.adminDefaultCompany;
    adminDefaultCompany.nit = nit;
    const transportServicesCreationPermissions = [
      Permission.cargo.createManualCargo,
      Permission.cargo.createCargoRequest,
      Permission.cargo.createMassiveCargo,
      Permission.cargo.createCargoFromRoute,
    ];
    const escortServicesCreationPermission = Permission.cargo.createEscortService;
    const satelliteLockServicesCreationPermission = Permission.cargo.createSatelliteLockService;
    adminDefaultCompany.modules.forEach((module) => {
      module.functionalities.forEach((funct) => {
        //Si tiene servicios de escolta se habilitan las funcionalidades de escolta
        if (this.authService.hasEscortServicesCompany && escortServicesCreationPermission === funct.name) funct.visible = true;
        //Si tiene servicios de candado satelital se habilitan las funcionalidades de candado satelital
        if (this.authService.hasSatelliteLockServicesCompany && satelliteLockServicesCreationPermission === funct.name) funct.visible = true;
        //Si no tiene servicios de transporte se deshabilitan las funcionalidades de transporte
        if (!this.authService.hasTransportServicesCompany && transportServicesCreationPermissions.includes(funct.name)) funct.visible = false;
      })
    });
    listRoles.push(adminDefaultCompany);

    let reader = this.modelRoleComany.reader;
    reader.nit = nit;
    listRoles.push(reader);

    listRoles.forEach((role) => {
      role.modules.forEach(module => {
        module.functionalities = module.functionalities.filter(functionality => !!functionality);
      })
    })

    return listRoles;
  }

  public getCostCenters(transportNit?: string): Observable<CostCenter[]> {
    let params = new HttpParams();
    if (transportNit) params = params.append('transportCompanyId', transportNit);
    return this.http.get<CostCenter[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.costCenters}`, { params }
    );
  }

  /** Centros de costo asociados a una empresa cliente, acotados por empresa de transporte. */
  public getCompanyCostCenters(companyId: string, transportCompanyId?: string): Observable<CostCenter[]> {
    let params = new HttpParams();
    if (transportCompanyId) params = params.append('transportCompanyId', transportCompanyId);
    return this.http.get<CostCenter[]>(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.individualCostCenter, companyId)}`, { params }
    );
  }

  public getPaymentTypes(transportNit?: string): Observable<PaymentType[]> {
    let params = new HttpParams();
    if (transportNit) params = params.append('transportCompanyId', transportNit);
    return this.http.get<PaymentType[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.paymentTypes}`, { params }
    )
  }

  /** Asocia (o crea, si no trae id) un centro de costo a la empresa cliente nit, para la empresa de transporte. */
  /** Responde la lista actualizada de centros asociados a la empresa. */
  public associateCostCenter(body: CostCenter, nit: string, transportCompanyId?: string): Observable<CostCenter[]> {
    return this.http.post<CostCenter[]>(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.individualCostCenter, nit)}`,
      body,
      { params: this.costCenterTransportParams(transportCompanyId) }
    );
  }

  /**
   * Resultado de asociar (POST) o eliminar (DELETE) un centro de costo. Responden la lista actualizada
   * de asociados; si llegara un objeto, es éxito sin message o con "Actualizado"/"Update", y cualquier
   * otro message es el error que informa el backend.
   */
  public isAssociateCostCenterSuccess(response: CostCenter[] | { message?: string }): boolean {
    if (Array.isArray(response)) return true;
    const message = response && response.message;
    return !message || message === 'Actualizado' || message === 'Update';
  }

  /** Centros asociados que trae la respuesta de asociar o eliminar; null si no los trae. */
  public costCentersFromResponse(response: CostCenter[] | { costCenter?: CostCenter[] }): CostCenter[] {
    if (Array.isArray(response)) return response;
    return response && Array.isArray(response.costCenter) ? response.costCenter : null;
  }

  /** Mensaje de error del backend en la respuesta, si lo trae. */
  public costCenterResponseMessage(response: CostCenter[] | { message?: string }): string {
    return response && !Array.isArray(response) && response.message ? response.message : null;
  }

  private costCenterTransportParams(transportCompanyId?: string): HttpParams {
    let params = new HttpParams();
    if (transportCompanyId) params = params.append('transportCompanyId', transportCompanyId);
    return params;
  }

  public uploadFilesCompany(paths: string[]) {
    return this.http.post(
      `${environment.urlServerTeclogi}${this.endpointResources.companyDocuments}`,
      {
        companyId: this.authService.getUserSession().clientCompanyId,
        documents: paths
      }
    );
  }

  public setCompanyState(companyId: string, active: boolean, description?: string): Observable<{ message: string }> {
    let params = new HttpParams();
    params = params.append('active', active.toString());
    if (description) params = params.append('description', description);
    return this.http.put<{ message: string }>(`${environment.urlServerTeclogi}${this.endpointResources.companyState.replace('%s', companyId)}`, {}, { params });
  }

  public async transformFilesToArrayPath(list: TreeFolder[]): Promise<any> {
    return new Promise(resolve => {
      let listPaths: string[] = [];
      let count = 1;
      list.forEach(async item => {
        await this.getLastPathChildren(item, listPaths);
        if (count === list.length) {
          listPaths = listPaths.filter((value, index, self) => {
            return self.indexOf(value) === index;
          }).filter((value) => {
            return value.length;
          });
          resolve(listPaths);
        } else {
          count++;
        }
      });
    });
  }

  private getLastPathChildren(item: TreeFolder, listPaths) {
    let path: string = '';
    if (item.children.length) {
      item.children.forEach(async (element) => {
        if (element.children.length) {
          this.getLastPathChildren(element, listPaths);
        } else {
          path = element.path;
        }
        listPaths.push(path);
      });
    } else {
      path = item.path;
      listPaths.push(path);
    }
  }

  public addEmailToCompany(body: EmailStructure, companyId: string) {
    return this.http.post(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.companyEmails, companyId)}`,
      body
    );
  }


  public getEmailsFromCompany(companyId: string): Observable<EmailStructure[]> {
    return this.http.get<EmailStructure[]>(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.companyEmails, companyId)}`
    );
  }

  public deleteEmailFromCompany(companyId: string, emailId: string) {
    return this.http.delete(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.companyEmails, companyId)}/${emailId}`
    );
  }

  public updateAlertsAndBlocks(body, companyId) {
    return this.http.put(
      `${environment.urlServerTeclogi}${this.endpointResources.updateAlertsAndBlocks}?companyId=${companyId}`,
      body
    );
  }

  public updatePolicies(rules: ValidationRule[]) {
    const endpoint = `${environment.urlServerTeclogi}${this.endpointResources.validationRules}`;
    return this.http.put(endpoint, rules);
  }

  setDaysToDeactivateVehicle(body) {
    return this.http.put(
      `${environment.urlServerTeclogi}${this.endpointResources.urlSetDaysDeactivateVehicle}`,
      body
    );
  }

  updateChargeOwnFleet(companyId: string, value: boolean) {
    return this.http.put(`${environment.urlServerTeclogi}${this.endpointResources.updateChargeOwnFleet}${companyId}?active=${value}`, {});
  }

  updateUseEvidenceToGuides(companyId: string, value: boolean) {
    const params = new HttpParams().set('useEvidenceToGuides', value.toString());
    return this.http.put(`${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.updateUseEvidenceToGuides, companyId)}`, {}, { params });
  }

  public setResume(company: string, validateTruora: boolean) {
    return this.http.put(
      `${environment.urlServerTeclogi}${this.endpointResources.urlSetResume}/${company}?active=${validateTruora}`, {}
    );
  }

  public getLabelTags(): Observable<Tag[]> {
    return this.http.get<any>(
      `${environment.urlServerTeclogi}${this.endpointResources.labelTags}`
    ).pipe(
      map(response => response.catalog) // Transforma la respuesta para extraer solo el arreglo 'catalog'
    );
  }

  public addLabelTag(body) {
    return this.http.post(
      `${environment.urlServerTeclogi}${this.endpointResources.addlabelTag}`,
      body
    );
  }

  public getCompanyCompliments(companyNit: string): Observable<CompanyCompliment[]> {
    return this.http.get<CompanyCompliment[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.compliments}companyId=${companyNit}`
    );
  }

  public getSaasTransportCompanies(): Observable<SaasTransportCompany[]> {
    return this.http.get<SaasTransportCompany[]>(
      `${environment.urlServerTeclogi}${Fmt.string(
        this.endpointResources.saasTransportCompanies,
        this.authService.getCompanySaaS().companyId)}`);
  }

  /**
   * Comparte el listado de transportadoras de la SaaS entre consumidores que
   * solo necesitan consultarlo. Delega en SaasTransportCompanyService, que es
   * el dueño de la caché.
   */
  public getCachedSaasTransportCompanies(): Observable<SaasTransportCompany[]> {
    return this.saasTransportCompanyService.list();
  }

  public updateSaasTransportCompanyInsuranceInformation(
    companyId: string,
    body: { insuranceInformation: InsuranceInformation, policyByCompany: boolean }
  ): Observable<SaasTransportCompany> {
    return this.http.put<SaasTransportCompany>(
      `${environment.urlServerTeclogi}${Fmt.string(
        this.endpointResources.saasTransportCompanyInsuranceInformation,
        this.authService.getCompanySaaS().companyId,
        companyId
      )}`,
      body
    ).pipe(tap(() => this.saasTransportCompanyService.clearCache()));
  }

  public setMonitorState(req: { companyId: string, status: string }) {
    return this.http.put(`${environment.urlServerTeclogi}${this.endpointResources.monitorState}`, req);
  }

  public setRequiredVehicleDocuments(body: VehicleDocumentRequired[]) {
    return this.http.put(`${environment.urlServerTeclogi}company/saas/${this.authService.getCompanySaaS().companyId}/vehicle-documents`, body);
  }

  public createCompanyCompliment(companyNit: string, body: CompanyComplimentDTO): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.urlServerTeclogi}${this.endpointResources.compliments}companyId=${companyNit}`,
      body
    );
  }

  public updateCompanyCompliment(companyNit: string, body: CompanyCompliment): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(
      `${environment.urlServerTeclogi}${this.endpointResources.compliments}companyId=${companyNit}`,
      body
    );
  }

  public getComplimentTitles(): Observable<ComplimentTitle[]> {
    return this.http.get<ComplimentTitle[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.getComplimentTypesCatalog}`
    );
  }

  public createComplimentTitle(title: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.urlServerTeclogi}${this.endpointResources.addPrivateComplimentType}`,
      [{ name: title }]
    );
  }

  /** Responde la lista actualizada de centros asociados a la empresa. */
  public deleteCostCenter(companyId: string, costCenterId: string, transportCompanyId?: string): Observable<CostCenter[]> {
    return this.http.delete<CostCenter[]>(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.individualCostCenter, companyId)}/${costCenterId}`,
      { params: this.costCenterTransportParams(transportCompanyId) }
    );
  }

  public assignCostCenterToService(cargoId: string, body: CostCenter) {
    return this.http.put(`${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.assignCostCenterToService, cargoId)}`, body)
  }

  public getGuideNovelties(companyId: string): Observable<GuideNovelty[]> {
    return this.http.get<GuideNovelty[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.guideNovelties}?companyId=${companyId}`
    );
  }

  public createGuideNovelty(body: GuideNovelty[]): Observable<GuideNovelty> {
    return this.http.post<GuideNovelty>(
      `${environment.urlServerTeclogi}${this.endpointResources.guideNovelties}`,
      body
    );
  }

  public updateGuideNovelty(body: GuideNovelty, idNovelty: string): Observable<GuideNovelty> {
    return this.http.put<GuideNovelty>(
      `${environment.urlServerTeclogi}${this.endpointResources.guideNovelties}/${idNovelty}`,
      body
    );
  }

  public listBillingUsers(pageKey: number, pageSize: number, transportCompanyId?: string): Observable<BillingUser[]> {
    const transportCompanyParam = transportCompanyId ? `&transportCompanyId=${transportCompanyId}` : '';
    return this.http.get<BillingUser[]>(`${environment.urlServerTeclogi}${this.endpointResources.listBillingUsers}?page=${pageKey}&size=${pageSize}${transportCompanyParam}`)
  }

  // El endpoint ya trae ?type=FV: transportCompanyId se agrega con &.
  public listTypeBill(transportCompanyId?: string): Observable<TypeBill[]> {
    const transportCompanyParam = transportCompanyId ? `&transportCompanyId=${transportCompanyId}` : '';
    return this.http.get<TypeBill[]>(`${environment.urlServerTeclogi}${this.endpointResources.listTypeBill}${transportCompanyParam}`)
  }

  /** Tipos de documento contable (comprobante contable, CC) de Siigo para la empresa de transporte. */
  public listAccountingDocumentTypes(transportCompanyId?: string): Observable<TypeBill[]> {
    return this.http.get<TypeBill[]>(`${environment.urlServerTeclogi}${this.endpointResources.listAccountingDocumentTypes}`,
      { params: this.transportCompanyParams(transportCompanyId) });
  }

  public updateTypeBillCompany(body: { documentTypeId?: { code: string, description: string }, expirationDays: number, requiresPreInvoice: boolean }, nitCompany: string, transportCompanyId?: string) {
    return this.http.put(`${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.updateTypeBillCompany, nitCompany)}`, body,
      { params: this.transportCompanyParams(transportCompanyId) })
  }

  public getPreBillFields(companyId: string): Observable<PreBillField[]> {
    return this.http.get<PreBillField[]>(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.companyPreBillFields, companyId)}`
    ).pipe(
      map((data: PreBillField[]) => Array.isArray(data) ? data : [])
    );
  }

  public savePreBillFields(companyId: string, fields: PreBillField[]): Observable<PreBillField[]> {
    return this.http.post<PreBillField[]>(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.companyPreBillFields, companyId)}`,
      fields
    );
  }

  public deletePreBillField(companyId: string, fieldId: string): Observable<any> {
    return this.http.delete(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.companyPreBillFields, companyId)}/${fieldId}`
    );
  }

  public updateProductSiigoAdditionalService(productSiigoId: string, additionalServiceId: string, transportCompanyId?: string) {
    return this.http.put(`${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.updateProductSiigoAdditionalService, additionalServiceId)}`, { productSiigoId },
      { params: this.transportCompanyParams(transportCompanyId) })
  }

  /** Query param de la empresa de transporte; vacío cuando no hay (comportamiento previo). */
  private transportCompanyParams(transportCompanyId?: string): HttpParams {
    let params = new HttpParams();
    if (transportCompanyId) params = params.append('transportCompanyId', transportCompanyId);
    return params;
  }

  public setInfoUserClient(document: string, siigoId: string) {
    return this.http.put(`${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.setInfoUserClient, document)}`, { siigoId })
  }

  public getConsecutive(type: 'Receipt' | 'Journal' | 'ReceiptFintech'): Observable<number> {
    return this.http.get<number>(`${environment.urlServerTeclogi}${this.endpointResources.getConsecutive}?type=${type}`)
  }

  public updateConsecutive(type: 'Receipt' | 'Journal' | 'ReceiptFintech', consecutive: number): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${environment.urlServerTeclogi}${this.endpointResources.updateConsecutive}?type=${type}&consecutive=${consecutive}`, {})
  }

  private checkExistentSubscription(companyId: string): { id: string, eventEmitter: EventEmitter<Company> } {
    const subscription = this.unsubscribeCompanyChanges.find(sub => sub.companyId === companyId);
    if (!subscription) return null;
    const newSubscription = { ...subscription, id: new Date().getTime().toString() };
    this.unsubscribeCompanyChanges.push(newSubscription);
    return { id: newSubscription.id, eventEmitter: newSubscription.eventEmitter };
  }

  public watch(companyId: string): { id: string, eventEmitter: EventEmitter<Company> } {
    const existentSubscription = this.checkExistentSubscription(companyId);
    if (existentSubscription) return existentSubscription;

    const eventEmitter = new EventEmitter<Company>();
    const activeSubscription = {
      companyId,
      id: new Date().getTime().toString(),
      unsubscribe: () => { },
      eventEmitter
    };
    const openSubscription = FirebaseApi.firestore.collection('Company').doc(companyId).onSnapshot((snapshot) => {
      const data = snapshot.data();
      if (data) {
        const sanitizedCompany = this.sanitizeCompany(data);
        if (sanitizedCompany) eventEmitter.emit(sanitizedCompany);
      }
    });

    activeSubscription.unsubscribe = openSubscription;
    this.unsubscribeCompanyChanges.push(activeSubscription);
    return { id: activeSubscription.id, eventEmitter };
  }

  private sanitizeCompany(company: Company): Company {
    if (!company) return null;
    Object.keys(company).forEach(key => {
      const keysToDelete = ['subjectEmail', 'bodyEmail'];
      if (company[key] === null || company[key] === undefined || keysToDelete.includes(key))
        delete company[key];
      else if (typeof company[key] === 'object') {
        if (Array.isArray(company[key])) {
          company[key] = company[key].map(item => {
            if (this.utils.isDefined(item) && typeof item === 'object')
              return this.sanitizeCompany(item);
            return item;
          });
        }
        else if (this.utils.isDefined(company[key].seconds) && this.utils.isDefined(company[key].nanoseconds))
          company[key] = this.transformCompanyDate(company[key], key);
        else {
          company[key] = this.sanitizeCompany(company[key]);
          if (!Array.isArray(company[key]) && Object.keys(company[key]).length === 0)
            delete company[key];
        }
      }
    });
    return company as Company;
  }

  private transformCompanyDate(item: { seconds: number, nanoseconds: number }, key: string): string {
    const date = new Date(item.seconds * 1000 + item.nanoseconds / 1000000);
    if (["expirationDate", "creationDate"].includes(key))
      return DateManager.dateToString(date, 'DD/MM/YYYY');
    else
      return DateManager.dateToString(date);
  }

  public stopWatch(id?: string) {
    if (id) {
      const subscription = this.unsubscribeCompanyChanges.findIndex(sub => sub.id === id);
      if (subscription !== -1) {
        const sub = this.unsubscribeCompanyChanges[subscription];
        const companyIdHasOtherSubscription = this.unsubscribeCompanyChanges.some(s => s.companyId === sub.companyId && s.id !== sub.id);
        if (!companyIdHasOtherSubscription) {
          sub.unsubscribe();
          sub.eventEmitter.complete();
        }
        this.unsubscribeCompanyChanges.splice(subscription, 1);
      }
    } else {
      this.unsubscribeCompanyChanges.forEach(sub => {
        sub.unsubscribe();
        sub.eventEmitter.complete();
      });
      this.unsubscribeCompanyChanges = [];
    };
  }
}
